import { Chip, Switch } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import type { WorkQueueEntityType } from './work-queues-types';

function EntityTypeDetailRow({
  label,
  children,
}: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <>
      <dt className="text-default-500">{label}</dt>
      <dd className="m-0 flex flex-wrap gap-1.5">{children}</dd>
    </>
  );
}

function EntityTypeCard({
  entityType,
  queueCount,
  onToggleEnabled,
}: Readonly<{
  entityType: WorkQueueEntityType;
  queueCount: number;
  onToggleEnabled: (key: string, enabled: boolean) => void;
}>) {
  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border border-default-200 p-4 min-w-0 ${
        entityType.is_enabled ? 'bg-content1' : 'bg-default-50'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-base font-semibold text-foreground">
          <Icon
            icon={entityType.icon}
            className="text-primary shrink-0"
            width={20}
          />
          {entityType.name}
        </span>
        <Switch
          size="sm"
          isSelected={entityType.is_enabled}
          aria-label={`Enable ${entityType.name}`}
          onValueChange={(next) => onToggleEnabled(entityType.key, next)}
        />
      </div>
      <p className="text-sm text-default-500">{entityType.description}</p>
      <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-sm items-start">
        <EntityTypeDetailRow label="Source">
          <span className="text-foreground">{entityType.source}</span>
        </EntityTypeDetailRow>
        <EntityTypeDetailRow label="Routing fields">
          {entityType.routing_fields.map((field) => (
            <Chip key={field} size="sm" variant="flat">
              {field}
            </Chip>
          ))}
        </EntityTypeDetailRow>
        <EntityTypeDetailRow label="Statuses">
          {entityType.statuses.map((status) => (
            <Chip key={status} size="sm" variant="flat">
              {status}
            </Chip>
          ))}
        </EntityTypeDetailRow>
        <EntityTypeDetailRow label="Queues">
          <span className="text-foreground tabular-nums">{queueCount}</span>
        </EntityTypeDetailRow>
      </dl>
    </div>
  );
}

export function EntityTypesTab({
  entityTypes,
  queueCountByEntityType,
  onToggleEnabled,
}: Readonly<{
  entityTypes: WorkQueueEntityType[];
  queueCountByEntityType: Record<string, number>;
  onToggleEnabled: (key: string, enabled: boolean) => void;
}>) {
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-default-200 bg-content1 p-6">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          Queue-enabled Entity Types
        </h2>
        <p className="mt-1 text-sm text-default-500">
          Turn on the record types that can be routed through work queues.
          Routing fields are what rules and territories match on.
        </p>
      </div>

      {entityTypes.length === 0 ? (
        <div className="py-16 text-center text-default-400">
          No entity types available.
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
          {entityTypes.map((entityType) => (
            <EntityTypeCard
              key={entityType.uuid}
              entityType={entityType}
              queueCount={queueCountByEntityType[entityType.uuid] ?? 0}
              onToggleEnabled={onToggleEnabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default EntityTypesTab;
